import { clientEnvModule } from "@/modules/client-env";
import { ThemeType } from "@/mds/themes/theme-context";
import {
  CalendarEvent,
  RecallMeetingDetectedEventKind,
  RecallPermissionKind,
  RecallPermissionStatus,
  ElectronAppInfo,
  RecallSdkState,
  ImportAppleNotesResult,
} from "@/modules/electron/types";
import { TrackedEvent } from "@/domains/metrics";

/**
 * Should match the definition in our `mem-client-desktop` project.
 */
type ElectronAPI = {
  navigateToInternalUrl: (args: { url: string }) => Promise<void>;
  navigateToExternalUrl: (args: { url: string }) => Promise<void>;
  toggleMenu: () => Promise<void>;
  minimizeWindow: () => Promise<void>;
  maximizeWindow: () => Promise<void>;
  closeWindow: () => Promise<void>;
  // Passing tells the handler to keep checking for updates, even if there isn't one available yet.
  updateNow: (waitForUpdate: boolean) => Promise<boolean>;
  restartApp?: () => Promise<void>;
  openSystemSoundSettings?: (args?: { focus?: "input" | "output" }) => Promise<void>;
  setTheme: (args: { theme: ThemeType }) => Promise<void>;
  setMeetingNotificationEnabled: (args: { enabled: boolean }) => Promise<void>;
  getMeetingNotificationEnabled: () => Promise<{ enabled: boolean }>;
  setTeamsMeetingDetectionEnabled: (args: { enabled: boolean }) => Promise<void>;
  getTeamsMeetingDetectionEnabled: () => Promise<{ enabled: boolean }>;
  setAdvancedMeetingDetectionShown: (args: { shown: boolean }) => Promise<void>;
  getAdvancedMeetingDetectionShown: () => Promise<{ shown: boolean }>;
  setClientMetadata?: (args: { context: Record<string, unknown> }) => Promise<void>;
  clearClientMetadata?: () => Promise<void>;
  getElectronAppInfo?: () => Promise<ElectronAppInfo>;
  onBrowserMetadata?: (
    cb: (metadata: {
      segmentAnonymousId?: string;
      gaClientId?: string;
      gaSessionId?: string;
    }) => void
  ) => Promise<() => void>;
  setCalendarEvents?: (args: { events: CalendarEvent[] }) => Promise<void>;
  onTrackedEvent?: (cb: (payload: { trackedEvent: TrackedEvent }) => void) => () => void;
  setIsClientReady?: (args: { isReady: boolean }) => Promise<void>;
  importAppleNotes?: () => Promise<ImportAppleNotesResult>;
  /**
   * Events related to the Recall.ai Desktop SDK
   */
  recall: {
    prepareDesktopRecording: () => Promise<{ windowId: string }>;
    prepareRecording: (args: {
      audioRecordingId: string;
      uploadToken: string;
      windowId: string;
    }) => Promise<void>;
    startRecording: (args: {
      windowId: string;
      uploadToken: string;
      prepared: boolean;
    }) => Promise<void>;
    pause: (args: { windowId: string }) => Promise<void>;
    resume: (args: { windowId: string }) => Promise<void>;
    stop: (args: { windowId: string }) => Promise<void>;
    onEvent?: (cb: (payload: { event: unknown }) => void) => () => void;
    onMeetingEvent?: (
      cb: (payload: {
        event: {
          window: { id: string; title?: string; url?: string; platform: string };
          info?: {
            startTime?: string | null;
            endTime?: string | null;
            attendees?: string[] | null;
          };
        };
        kind: RecallMeetingDetectedEventKind;
        debug: boolean;
      }) => void
    ) => () => void;
    requestPermission: (permission: RecallPermissionKind) => Promise<void>;
    getPermissionStatus: () => Promise<Record<RecallPermissionKind, RecallPermissionStatus>>;
    onPermissionStatus?: (
      cb: (payload: {
        event: { permission: RecallPermissionKind; status: RecallPermissionStatus };
      }) => void
    ) => () => void;
    openPermissionPane: (permission: RecallPermissionKind) => Promise<void>;
    getSdkState: () => Promise<{ state: RecallSdkState; errorMessage?: string }>;
    onSdkState?: (
      cb: (payload: { state: RecallSdkState; errorMessage?: string }) => void
    ) => () => void;
    testUnexpectedShutdown: () => Promise<void>;
  };
};

export const getElectronAPI = (): ElectronAPI | null => {
  const isDesktop = clientEnvModule.isDesktop();

  if (isDesktop) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any).electronAPI as ElectronAPI;
  }

  return null;
};
